<template>
  <div class="min-h-screen bg-gray-900">
    <!-- Header -->
    <div class="bg-gray-800 shadow-sm border-b border-gray-700">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center py-4">
          <router-link
            to="/register"
            class="text-indigo-400 hover:text-indigo-300 flex items-center"
          >
            <svg
              class="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Nazad na registraciju
          </router-link>

          <h1 class="text-xl font-bold text-white">Moja Garderoba</h1>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="bg-gray-800 rounded-lg shadow-lg">
        <!-- Page Header -->
        <div class="bg-indigo-600 px-6 py-8 text-center">
          <h1 class="text-3xl font-bold text-white mb-2">
            Politika privatnosti
          </h1>
          <p class="text-indigo-100">Kako čuvamo i koristimo vaše podatke</p>
          <p class="text-indigo-200 text-sm mt-2">
            Poslednja izmena: {{ getCurrentDate() }}
          </p>
        </div>

        <!-- Content -->
        <div class="p-4 sm:p-6 lg:p-8 text-gray-300 space-y-8 min-w-0">
          <!-- Uvod -->
          <div>
            <p class="text-lg leading-relaxed">
              Ova politika privatnosti objašnjava kako prikupljamo, koristimo,
              čuvamo i štitimo vaše lične podatke kada koristite našu platformu
              <strong class="text-white">Moja Garderoba</strong>
              (www.mojagarderoba.rs). Vaša privatnost je za nas od ključne
              važnosti i posvećeni smo transparentnom rukovanju vašim podacima.
            </p>
          </div>

          <!-- 1. Koje podatke prikupljamo -->
          <section>
            <h2 class="text-2xl font-bold text-white mb-4 flex items-center">
              <span
                class="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3"
                >1</span
              >
              Koje podatke prikupljamo
            </h2>

            <div class="space-y-4">
              <div>
                <h3 class="text-lg font-semibold text-indigo-400 mb-2">
                  📧 Podaci o nalogu
                </h3>
                <ul class="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Email adresa</strong> - obavezno za registraciju i
                    komunikaciju
                  </li>
                  <li>
                    <strong>Lozinka</strong> - enkriptovano skladištena za
                    bezbednost naloga
                  </li>
                  <li>
                    <strong>Ime i prezime</strong> - za identifikaciju i
                    poverenje među korisnicima
                  </li>
                  <li>
                    <strong>Korisničko ime (username)</strong> - javno ime na
                    platformi
                  </li>
                  <li>
                    <strong>Mesto stanovanja</strong> - za lakše povezivanje
                    lokalnih korisnika
                  </li>
                  <li>
                    <strong>Broj telefona</strong> - opciono, za direktnu
                    komunikaciju
                  </li>
                </ul>
              </div>

              <div>
                <h3 class="text-lg font-semibold text-indigo-400 mb-2">
                  🛍️ Podaci o oglasima
                </h3>
                <ul class="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Slike proizvoda</strong> - upload na Bunny CDN za
                    brže učitavanje
                  </li>
                  <li>
                    <strong>Opisi artikala</strong> - detaljne informacije o
                    proizvodu
                  </li>
                  <li>
                    <strong>Cene i kategorije</strong> - za organizaciju i
                    pretraživanje
                  </li>
                  <li>
                    <strong>Stanje artikla</strong> - informacije o kvalitetu
                    proizvoda
                  </li>
                </ul>
              </div>

              <div>
                <h3 class="text-lg font-semibold text-indigo-400 mb-2">
                  💬 Komunikacija
                </h3>
                <ul class="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Chat poruke</strong> - između kupaca i prodavaca
                  </li>
                  <li>
                    <strong>Ocene i komentari</strong> - feedback o
                    transakcijama
                  </li>
                  <li>
                    <strong>Prijave sadržaja</strong> - za moderaciju platforme
                  </li>
                </ul>
              </div>

              <div>
                <h3 class="text-lg font-semibold text-indigo-400 mb-2">
                  🔧 Tehnički podaci
                </h3>
                <ul class="list-disc pl-6 space-y-2">
                  <li>
                    <strong>IP adresa</strong> - za bezbednost i sprečavanje
                    zloupotrebe
                  </li>
                  <li>
                    <strong>Tip uređaja i browser</strong> - za optimizaciju
                    korisničkog iskustva
                  </li>
                  <li>
                    <strong>Podaci o korišćenju</strong> - anonimni analytics za
                    poboljšanje platforme
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <!-- 2. Kako koristimo podatke -->
          <section>
            <h2 class="text-2xl font-bold text-white mb-4 flex items-center">
              <span
                class="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3"
                >2</span
              >
              Kako koristimo vaše podatke
            </h2>

            <div class="space-y-4 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0">
              <div class="bg-gray-700 p-4 rounded-lg">
                <h3 class="font-semibold text-white mb-2">
                  🏪 Marketplace funkcionalnost
                </h3>
                <ul class="list-disc pl-4 space-y-1 text-sm">
                  <li>Kreiranje i održavanje korisničkih naloga</li>
                  <li>Prikazivanje oglasa drugim korisnicima</li>
                  <li>Omogućavanje pretrage i filtriranja</li>
                  <li>Facilitiranje komunikacije između korisnika</li>
                </ul>
              </div>

              <div class="bg-gray-700 p-4 rounded-lg">
                <h3 class="font-semibold text-white mb-2">
                  🔒 Bezbednost i poverenje
                </h3>
                <ul class="list-disc pl-4 space-y-1 text-sm">
                  <li>Verifikacija identiteta korisnika</li>
                  <li>Sprečavanje prevara i zloupotrebe</li>
                  <li>Moderacija sadržaja i oglasa</li>
                  <li>Rešavanje sporova između korisnika</li>
                </ul>
              </div>

              <div class="bg-gray-700 p-4 rounded-lg">
                <h3 class="font-semibold text-white mb-2">📧 Komunikacija</h3>
                <ul class="list-disc pl-4 space-y-1 text-sm">
                  <li>Slanje verifikacionih email-ova</li>
                  <li>Obaveštenja o novim porukama</li>
                  <li>Važne informacije o nalogu</li>
                  <li>Odgovori na korisničke upite</li>
                </ul>
              </div>

              <div class="bg-gray-700 p-4 rounded-lg">
                <h3 class="font-semibold text-white mb-2">
                  📊 Poboljšanje servisa
                </h3>
                <ul class="list-disc pl-4 space-y-1 text-sm">
                  <li>Analiza korišćenja platforme</li>
                  <li>Optimizacija performansi</li>
                  <li>Razvoj novih funkcionalnosti</li>
                  <li>Personalizacija korisničkog iskustva</li>
                </ul>
              </div>
            </div>
          </section>

          <!-- 3. Deljenje podataka -->
          <section>
            <h2 class="text-2xl font-bold text-white mb-4 flex items-center">
              <span
                class="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3"
                >3</span
              >
              Deljenje podataka sa trećim stranama
            </h2>

            <div
              class="bg-green-900 border border-green-700 p-4 rounded-lg mb-4"
            >
              <h3 class="font-semibold text-green-300 mb-2">
                ✅ Naša obaveza prema vama
              </h3>
              <p class="text-green-100">
                <strong>NE PRODAJEMO</strong> vaše lične podatke trećim
                stranama. <strong>NE IZNAJMLJUJEMO</strong> vaše informacije
                reklamnim kompanijama. Vaša privatnost nije na prodaju.
              </p>
            </div>

            <div class="space-y-4">
              <div>
                <h3 class="text-lg font-semibold text-indigo-400 mb-2">
                  🌐 Javno dostupni podaci
                </h3>
                <p>Sledeći podaci su namerno javni na platformi:</p>
                <ul class="list-disc pl-6 space-y-1 mt-2">
                  <li>Korisničko ime i osnovne informacije profila</li>
                  <li>Oglasi koje postavite (slike, opisi, cene)</li>
                  <li>Javne ocene i komentari</li>
                  <li>Mesto stanovanja (približno, bez tačne adrese)</li>
                </ul>
              </div>

              <div>
                <h3 class="text-lg font-semibold text-indigo-400 mb-2">
                  ⚙️ Tehnički servisi
                </h3>
                <p>Koristimo pouzdane pružaoce usluga:</p>
                <ul class="list-disc pl-6 space-y-1 mt-2">
                  <li>
                    <strong>Bunny CDN</strong> - za čuvanje i dostavljanje slika
                    (GDPR kompatibilno)
                  </li>
                  <li>
                    <strong>Email servisi</strong> - za slanje verifikacionih
                    poruka
                  </li>
                  <li>
                    <strong>Hosting provajderi</strong> - za čuvanje podataka u
                    sigurnim centrima
                  </li>
                </ul>
                <p class="text-sm text-gray-400 mt-2">
                  Svi naši partneri potpisuju ugovore o zaštiti podataka i sledе
                  stroge bezbednosne standarde.
                </p>
              </div>

              <div>
                <h3 class="text-lg font-semibold text-indigo-400 mb-2">
                  ⚖️ Zakonske obaveze
                </h3>
                <p>Podatke možemo podeliti samo u sledećim slučajevima:</p>
                <ul class="list-disc pl-6 space-y-1 mt-2">
                  <li>Na osnovu sudske naredbe ili zakonskog zahteva</li>
                  <li>Za zaštitu bezbednosti korisnika ili javnosti</li>
                  <li>U slučaju istrage kriminalnih aktivnosti</li>
                </ul>
              </div>
            </div>
          </section>

          <!-- 4. Bezbednost podataka -->
          <section>
            <h2 class="text-2xl font-bold text-white mb-4 flex items-center">
              <span
                class="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3"
                >4</span
              >
              Kako štitimo vaše podatke
            </h2>

            <div class="space-y-6 lg:grid lg:grid-cols-2 lg:gap-8 lg:space-y-0">
              <div>
                <h3 class="text-lg font-semibold text-indigo-400 mb-4">
                  🔐 Tehnička zaštita
                </h3>
                <ul class="space-y-3 text-sm">
                  <li class="flex items-start">
                    <span class="text-green-400 mr-3 mt-1">✓</span>
                    <span>
                      <strong>HTTPS šifrovanje</strong> - sav saobraćaj je
                      enkriptovan</span
                    >
                  </li>
                  <li class="flex items-start">
                    <span class="text-green-400 mr-3 mt-1">✓</span>
                    <span>
                      <strong>Enkriptovane lozinke</strong> - koristi se bcrypt
                      algoritam</span
                    >
                  </li>
                  <li class="flex items-start">
                    <span class="text-green-400 mr-3 mt-1">✓</span>
                    <span>
                      <strong>Sigurni serveri</strong> - u sertifikovanim data
                      centrima</span
                    >
                  </li>
                  <li class="flex items-start">
                    <span class="text-green-400 mr-3 mt-1">✓</span>
                    <span>
                      <strong>Redovno ažuriranje</strong> - bezbednosni
                      patch-ovi</span
                    >
                  </li>
                </ul>
              </div>

              <div>
                <h3 class="text-lg font-semibold text-indigo-400 mb-4">
                  👥 Organizaciona zaštita
                </h3>
                <ul class="space-y-3 text-sm">
                  <li class="flex items-start">
                    <span class="text-green-400 mr-3 mt-1">✓</span>
                    <span>
                      <strong>Ограничen pristup</strong> - samo ovlašćeno
                      osoblje</span
                    >
                  </li>
                  <li class="flex items-start">
                    <span class="text-green-400 mr-3 mt-1">✓</span>
                    <span>
                      <strong>Redovne bezbednosne obuke</strong> - za sve
                      zaposlene</span
                    >
                  </li>
                  <li class="flex items-start">
                    <span class="text-green-400 mr-3 mt-1">✓</span>
                    <span>
                      <strong>Monitoring pristupa</strong> - logovanje svih
                      aktivnosti</span
                    >
                  </li>
                  <li class="flex items-start">
                    <span class="text-green-400 mr-3 mt-1">✓</span>
                    <span>
                      <strong>Incident response plan</strong> - brz odgovor na
                      pretnje</span
                    >
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <!-- 5. Vaša prava -->
          <section>
            <h2 class="text-2xl font-bold text-white mb-4 flex items-center">
              <span
                class="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3"
                >5</span
              >
              Vaša prava u vezi sa podacima
            </h2>

            <div class="bg-blue-900 border border-blue-700 p-4 rounded-lg mb-4">
              <h3 class="font-semibold text-blue-300 mb-2">
                📋 Vaša prava prema GDPR-u
              </h3>
              <p class="text-blue-100">
                Kao korisnik imate prava koja su zagarantovana zakonima o
                zaštiti podataka. Poštujemo sva vaša prava i omogućavamo vam
                njihovo korišćenje.
              </p>
            </div>

            <div class="space-y-4 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0">
              <div class="bg-gray-700 p-4 rounded-lg">
                <h3 class="font-semibold text-white mb-2">
                  👁️ Pravo na pristup
                </h3>
                <p class="text-sm text-gray-300">
                  Možete zatražiti kopiju svih svojih podataka koje čuvamo.
                  Odgovorićemo u roku od 30 dana.
                </p>
              </div>

              <div class="bg-gray-700 p-4 rounded-lg">
                <h3 class="font-semibold text-white mb-2">
                  ✏️ Pravo na ispravku
                </h3>
                <p class="text-sm text-gray-300">
                  Možete zatražiti ispravku netačnih ili nepotpunih podataka o
                  vama.
                </p>
              </div>

              <div class="bg-gray-700 p-4 rounded-lg">
                <h3 class="font-semibold text-white mb-2">
                  🗑️ Pravo na brisanje
                </h3>
                <p class="text-sm text-gray-300">
                  Možete zatražiti brisanje svojih podataka u određenim
                  slučajevima.
                </p>
              </div>

              <div class="bg-gray-700 p-4 rounded-lg">
                <h3 class="font-semibold text-white mb-2">
                  📦 Pravo na prenos
                </h3>
                <p class="text-sm text-gray-300">
                  Možete dobiti svoje podatke u mašinski čitljivom formatu.
                </p>
              </div>
            </div>
          </section>

          <!-- 6. Kontakt -->
          <section>
            <h2 class="text-2xl font-bold text-white mb-4 flex items-center">
              <span
                class="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3"
                >6</span
              >
              Kontaktirajte nas
            </h2>

            <div class="bg-gray-700 p-6 rounded-lg">
              <p class="text-gray-300 mb-4">
                Za sva pitanja o politici privatnosti ili vašim podacima,
                kontaktirajte nas:
              </p>
              <div class="space-y-2">
                <p class="flex items-center">
                  <span class="text-indigo-400 mr-2">📧</span>
                  <strong>Email:</strong>
                  <a
                    href="mailto:devfinity.solutions.contact@gmail.com"
                    class="text-indigo-400 hover:text-indigo-300 ml-2"
                    >devfinity.solutions.contact@gmail.com</a
                  >
                </p>
                <p class="flex items-center">
                  <span class="text-indigo-400 mr-2">🌐</span>
                  <strong>Website:</strong>
                  <span class="ml-2">www.mojagarderoba.rs</span>
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

// Method to get current date in Serbian format
const getCurrentDate = (): string => {
  const date = new Date();
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  };

  return date.toLocaleDateString('sr-RS', options);
};
</script>
